import * as React from 'react'

import { animate } from 'motion/react'
import type { AnimationOptions } from 'motion/react'

/**
 * Custom hook to animate number transitions using the FLIP technique
 *
 * This hook implements the FLIP (First, Last, Invert, Play) animation technique
 * to create smooth vertical transitions between numbers.
 *
 * The animation works by:
 * 1. First: Measuring the initial position
 * 2. Last: Calculating the final position (y: 0)
 * 3. Invert: Computing the transform needed to make the number appear to start from its old position
 * 4. Play: Animating to the final position
 */
export const useAnimateNumber = (
  animateRef: React.RefObject<HTMLSpanElement>,
  widthRef: React.RefObject<HTMLSpanElement>,
  value: number,
  initialValue: number,
  transition: AnimationOptions,
): void => {
  const prevValue = React.useRef(initialValue)

  React.useLayoutEffect(() => {
    if (!animateRef.current || value === prevValue.current) return

    const box = animateRef.current.getBoundingClientRect()
    const refBox = widthRef.current?.getBoundingClientRect()

    const deltaTop = box.top - (refBox?.top ?? box.top)
    const initialY = box.height * (value - prevValue.current) + deltaTop

    // ✨ animate number ✨
    animate(animateRef.current, { y: [initialY, 0] }, transition)

    return (): void => {
      prevValue.current = value
    }
  }, [value, transition, animateRef, widthRef])
}
